import { ATTACK_PATH_COLUMNS, type AttackPathLink, type AttackPathNode, type AttackPathsModel } from './attack-paths.ts'

/**
 * Places the attack-path columns, and shapes the curves between them. Pure — no DOM, no React — so the
 * arithmetic can be tested without rendering anything.
 *
 * **Rows keep payload order.** Not sorted, and not reordered to reduce crossings: the generator's order
 * is the order the Lists view shows one tab away, and the same actor being third in both places is worth
 * more than a tidier picture. Crossing-minimisation would also be optimising the layout against the
 * placeholder edges, so it would visibly rearrange itself the day the real references arrive.
 *
 * **Height grows with the tallest column** rather than being fixed. The design mock divides a fixed
 * canvas by the row count, which holds at nine rows and collapses past that — twenty entry points would
 * leave 21px of box for two lines of text. Since the viewport pans and zooms, a taller canvas costs
 * nothing and keeps every row legible.
 */

/**
 * Wider than the five columns strictly need. At 1200 the gap between one card's right edge and the next
 * card's left was ~70px, so nineteen curves crossed each other inside it and the picture read as hatching
 * rather than as paths. The extra width all goes into those gaps — the cards themselves do not grow — and
 * the viewport scales the whole thing to fit, so a wider canvas costs nothing on screen.
 */
const WIDTH = 1560
const NODE_WIDTH = 174
const NODE_HEIGHT = 46
const ROW_GAP = 26
const ROW_PITCH = NODE_HEIGHT + ROW_GAP
/** Room for the column headings above the first row. */
const HEADER_HEIGHT = 92
const BOTTOM_PAD = 24
const SIDE_PAD = 26

export const ATTACK_PATH_NODE_RADIUS = 7
export const ATTACK_PATH_NODE_SIZE = { width: NODE_WIDTH, height: NODE_HEIGHT }

export type PlacedAttackPathNode = AttackPathNode & {
  x: number
  y: number
  /** Right edge and vertical centre — where a curve leaves this node. */
  exitX: number
  centerY: number
}

export type PlacedAttackPathLink = AttackPathLink & {
  /** SVG path data for the whole curve. */
  d: string
  /**
   * The curve's midpoint. Because the columns are evenly spaced, an entry-point-to-weakness curve
   * crosses the security-control column right there, so it is where a gate bar stands in the route.
   */
  gate: { at: { x: number; y: number } }
}

export type PlacedAttackPathColumn = {
  key: (typeof ATTACK_PATH_COLUMNS)[number]['key']
  title: string
  x: number
  count: number
}

export type AttackPathLayout = {
  contentSize: { width: number; height: number }
  columns: PlacedAttackPathColumn[]
  nodes: PlacedAttackPathNode[]
  links: PlacedAttackPathLink[]
}

/** A column's rows occupy this much vertical space, with no trailing gap after the last one. */
const columnHeight = (count: number): number => (count === 0 ? 0 : count * ROW_PITCH - ROW_GAP)

/**
 * A cubic with both control points on the midline, which is the standard shape for this diagram: it
 * leaves and arrives horizontally, so a curve reads as continuing the row it came from.
 */
export const curveBetween = (from: PlacedAttackPathNode, to: PlacedAttackPathNode): string => {
  const midX = (from.exitX + to.x) / 2
  return `M${from.exitX},${from.centerY} C${midX},${from.centerY} ${midX},${to.centerY} ${to.x},${to.centerY}`
}

/**
 * Where the curve crosses the column halfway between its ends. Both control points sit on the midline,
 * so the cubic's `t = 0.5` point is exactly `((exitX + toX) / 2, (fromY + toY) / 2)`.
 */
const curveMidpoint = (from: PlacedAttackPathNode, to: PlacedAttackPathNode) => ({
  x: (from.exitX + to.x) / 2,
  y: (from.centerY + to.centerY) / 2,
})

export const layOutAttackPaths = ({ nodes, links }: AttackPathsModel): AttackPathLayout => {
  // Only columns that actually hold nodes are placed, so a pre-linking document draws two
  // evenly-spaced columns — controls and weaknesses — rather than five with three empty ones. Order
  // follows ATTACK_PATH_COLUMNS, so whatever subset is drawn still reads left to right, and the even
  // spacing keeps an entry→weakness curve crossing the control column at its midpoint.
  const activeColumns = ATTACK_PATH_COLUMNS.filter(column => nodes.some(node => node.column === column.key))

  // One column has no gap to divide; place it flush left. In practice the graph always carries at
  // least controls and weaknesses, so this is a guard rather than a common path.
  const columnStep = activeColumns.length > 1 ? (WIDTH - 2 * SIDE_PAD - NODE_WIDTH) / (activeColumns.length - 1) : 0

  const byColumn = activeColumns.map((column, columnIndex) => ({
    ...column,
    x: SIDE_PAD + columnIndex * columnStep,
    members: nodes.filter(node => node.column === column.key),
  }))

  const tallest = Math.max(...byColumn.map(column => column.members.length), 0)
  const bandHeight = columnHeight(tallest)
  const height = HEADER_HEIGHT + bandHeight + BOTTOM_PAD

  // Each column is centred in the band, so a short column sits opposite the middle of a long one
  // rather than hanging from the top and leaving the curves to climb.
  const placed: PlacedAttackPathNode[] = byColumn.flatMap(column => {
    const top = HEADER_HEIGHT + (bandHeight - columnHeight(column.members.length)) / 2
    return column.members.map((node, rowIndex) => {
      const y = top + rowIndex * ROW_PITCH
      return {
        ...node,
        x: column.x,
        y,
        exitX: column.x + NODE_WIDTH,
        centerY: y + NODE_HEIGHT / 2,
      }
    })
  })

  const positions = new Map(placed.map(node => [node.id, node]))

  return {
    contentSize: { width: WIDTH, height },
    columns: byColumn.map(({ key, title, x, members }) => ({ key, title, x, count: members.length })),
    nodes: placed,
    // A link naming a node this document does not contain is dropped rather than drawn to nowhere.
    links: links.flatMap(link => {
      const from = positions.get(link.from)
      const to = positions.get(link.to)
      if (!from || !to) {
        return []
      }
      return [{ ...link, d: curveBetween(from, to), gate: { at: curveMidpoint(from, to) } }]
    }),
  }
}

/**
 * Every node and edge reachable from one node, walking forwards and backwards separately so the whole
 * chain through it lights up rather than only what follows it. Direction is read from column position
 * rather than from edge order, so it stays correct for the curves that skip the control column.
 */
export const traceAttackPath = (
  startId: string,
  layout: AttackPathLayout
): { nodeIds: Set<string>; linkKeys: Set<string> } => {
  const nodeIds = new Set([startId])
  const linkKeys = new Set<string>()
  const positions = new Map(layout.nodes.map(node => [node.id, node]))

  const walk = (direction: 'forward' | 'back') => {
    let frontier = [startId]
    const seen = new Set([startId])
    while (frontier.length > 0) {
      const next: string[] = []
      for (const id of frontier) {
        for (const link of layout.links) {
          const isFrom = link.from === id
          const otherId = isFrom ? link.to : link.from
          if (!isFrom && link.to !== id) {
            continue
          }
          const here = positions.get(id)
          const there = positions.get(otherId)
          if (!here || !there) {
            continue
          }
          const isForward = there.x > here.x
          if (direction === 'forward' ? !isForward : isForward) {
            continue
          }
          linkKeys.add(attackPathLinkKey(link))
          nodeIds.add(otherId)
          if (!seen.has(otherId)) {
            seen.add(otherId)
            next.push(otherId)
          }
        }
      }
      frontier = next
    }
  }

  walk('forward')
  walk('back')
  return { nodeIds, linkKeys }
}

/** One defended route: `entry` exposes the weakness, and `control` guards `entry` and names it. */
export type GuardRoute = {
  entry: PlacedAttackPathNode
  control: PlacedAttackPathNode
  /** The control→weakness link the route leaves the control card by. */
  mitigation: PlacedAttackPathLink
}

export type FocusGuards = {
  routes: GuardRoute[]
  /**
   * Controls touching the focus that stand on no route: with an entry point focused, a control guarding
   * it that names none of its weaknesses (entry→control); with a weakness focused, a control naming it
   * that guards none of the entry points exposing it (control→weakness). Not linked to a path — though they may still apply — so drawn as ties.
   */
  ties: { from: PlacedAttackPathNode; to: PlacedAttackPathNode }[]
  /** Every control lifted out of the dimmed column: the routed ones, plus the tied ones. */
  controls: PlacedAttackPathNode[]
  /**
   * Keys of traced links drawn here instead of as plain traced curves: the direct entry→weakness links
   * a route through a control replaces, and — with a weakness focused — every control→weakness link
   * into it, which the trace walks back into but which is drawn as a route or a tie.
   */
  replacedLinkKeys: Set<string>
  /**
   * Focused weaknesses every route into which runs through a control. Only past these is the attack
   * conditional on a control failing; one direct route and the weakness is reached regardless.
   */
  fullyGatedVulnIds: Set<string>
}

/**
 * The controls standing on the focused node's routes, as (entry point, control, weakness) triples.
 *
 * A route is kept only when all three hold: the control's `applies_to` names the entry point, its
 * `mitigates` names the weakness, and the entry point `exposes` that weakness. Routing a path through a
 * control card is honest only when the focus pins one end of every route — one entry point (one source)
 * or one weakness (one target). Then lines converging on, or fanning out of, a control card all share
 * that end, so no entry→control→weakness chain can be read off the picture that the payload does not
 * state. With both ends free (an actor, control or asset focused) the card becomes a hub that implies
 * every pairing, so this returns `undefined` and the graph keeps its class-level bars.
 *
 * Also `undefined` for documents without `applies_to` (no `guardedBy` on the entry points).
 */
export const traceFocusGuards = (focusId: string, layout: AttackPathLayout): FocusGuards | undefined => {
  const focused = layout.nodes.find(node => node.id === focusId)
  if (!focused || (focused.column !== 'entry' && focused.column !== 'vuln')) {
    return undefined
  }
  const nodeById = new Map(layout.nodes.map(node => [node.id, node]))
  const exposures = layout.links.filter(link =>
    focused.column === 'entry' ? link.from === focused.id : link.to === focused.id && link.from.startsWith('entry:')
  )
  const entries = focused.column === 'entry' ? [focused] : exposures.flatMap(link => nodeById.get(link.from) ?? [])
  if (entries.length === 0 || entries.some(entry => !entry.guardedBy)) {
    return undefined
  }

  const routes = exposures.flatMap(exposure => {
    const entry = nodeById.get(exposure.from)
    return layout.links.flatMap(mitigation => {
      const control = nodeById.get(mitigation.from)
      const isRoute =
        mitigation.to === exposure.to && control?.column === 'control' && !!entry?.guardedBy?.includes(control.id)
      return isRoute && entry && control ? [{ entry, control, mitigation }] : []
    })
  })
  const routedControlIds = new Set(routes.map(route => route.control.id))
  const mitigationsOfFocus =
    focused.column === 'vuln'
      ? layout.links.filter(link => link.to === focused.id && nodeById.get(link.from)?.column === 'control')
      : []
  const ties =
    focused.column === 'entry'
      ? layout.nodes
          .filter(node => focused.guardedBy?.includes(node.id) && !routedControlIds.has(node.id))
          .map(control => ({ from: focused, to: control }))
      : mitigationsOfFocus.flatMap(link => {
          const control = nodeById.get(link.from)
          return control && !routedControlIds.has(control.id) ? [{ from: control, to: focused }] : []
        })
  const tiedControlIds = new Set(ties.map(tie => (tie.from.column === 'control' ? tie.from.id : tie.to.id)))
  const isGated = (exposure: AttackPathLink) =>
    routes.some(route => route.entry.id === exposure.from && route.mitigation.to === exposure.to)

  return {
    routes,
    ties,
    controls: layout.nodes.filter(node => routedControlIds.has(node.id) || tiedControlIds.has(node.id)),
    replacedLinkKeys: new Set([...exposures.filter(isGated), ...mitigationsOfFocus].map(attackPathLinkKey)),
    fullyGatedVulnIds: new Set(
      [...new Set(exposures.map(exposure => exposure.to))].filter(vulnId =>
        exposures.filter(exposure => exposure.to === vulnId).every(isGated)
      )
    ),
  }
}

export type TracedRouteGates = {
  /** Traced entry→weakness links a control on that route names: dashed, with a bar where they cross. */
  gatedRouteKeys: Set<string>
  /** Traced control→weakness links that stand on at least one traced route: dashed. */
  onRouteMitigationKeys: Set<string>
  /** Traced control→weakness links that stand on none of the traced routes into that weakness: ties. */
  offRouteMitigationKeys: Set<string>
  /** Traced weaknesses every traced route into which is gated: only past these is the attack conditional. */
  defendedVulnIds: Set<string>
}

/**
 * Per-route gating for a trace from any focus — the rule `traceFocusGuards` applies, used where the
 * path is not routed through control cards (actor, control or asset focus) but still marked per link.
 *
 * A traced entry→weakness link is gated only when a control both guards that entry point
 * (`applies_to`) and names that weakness (`mitigates`). A bar marks one specific link, so unlike routing
 * through a card this needs no pinned end: it cannot imply a pairing the payload does not state. A
 * weakness is defended only when every traced entry→weakness link into it is gated; when the trace
 * reaches it only from a control (a control focus), the control's own claim stands.
 *
 * `undefined` for documents without `applies_to`, which keep the class-level rule.
 */
export const gateTracedRoutes = (
  traced: { nodeIds: Set<string>; linkKeys: Set<string> },
  layout: AttackPathLayout
): TracedRouteGates | undefined => {
  const nodeById = new Map(layout.nodes.map(node => [node.id, node]))
  if (!layout.nodes.some(node => node.guardedBy !== undefined)) {
    return undefined
  }
  const tracedLinks = layout.links.filter(link => traced.linkKeys.has(attackPathLinkKey(link)))
  const routes = tracedLinks.filter(link => nodeById.get(link.from)?.column === 'entry')
  const mitigations = tracedLinks.filter(link => nodeById.get(link.from)?.column === 'control')
  const gatesOf = (route: AttackPathLink) =>
    layout.links.filter(
      link =>
        link.to === route.to &&
        nodeById.get(link.from)?.column === 'control' &&
        !!nodeById.get(route.from)?.guardedBy?.includes(link.from)
    )
  const gatedRoutes = routes.filter(route => gatesOf(route).length > 0)
  const gatedRouteKeys = new Set(gatedRoutes.map(attackPathLinkKey))
  const gatingKeys = new Set(gatedRoutes.flatMap(gatesOf).map(attackPathLinkKey))
  // A mitigation into a weakness the trace reaches by some route is off-route unless it gates one of
  // them; one into a weakness reached by no traced route (a control focus) keeps the control's claim.
  const isOffRoute = (mitigation: AttackPathLink) =>
    routes.some(route => route.to === mitigation.to) && !gatingKeys.has(attackPathLinkKey(mitigation))
  const vulnIds = new Set([...routes, ...mitigations].map(link => link.to))

  return {
    gatedRouteKeys,
    onRouteMitigationKeys: new Set(mitigations.filter(link => !isOffRoute(link)).map(attackPathLinkKey)),
    offRouteMitigationKeys: new Set(mitigations.filter(isOffRoute).map(attackPathLinkKey)),
    defendedVulnIds: new Set(
      [...vulnIds].filter(vulnId => {
        const into = routes.filter(route => route.to === vulnId)
        return into.length > 0 ? into.every(route => gatedRouteKeys.has(attackPathLinkKey(route))) : true
      })
    ),
  }
}

export const attackPathLinkKey = (link: Pick<AttackPathLink, 'from' | 'to'>): string => `${link.from}->${link.to}`
